import { forwardRef, ReactNode, Ref } from 'react';

// material-ui
import MuiButton from '@mui/material/Button';
import { styled, useTheme, SxProps, Theme } from '@mui/material/styles';
import { ButtonProps as MuiButtonProps } from '@mui/material';
import { CircularProgress } from '@mui/material';

// project imports
import getColors from 'utils/getColors';

// types
import { ButtonVariantProps } from 'types/extended';

// ==============================|| STYLED - BUTTON ||============================== //

interface StyledButtonProps {
  theme: Theme;
  variant?: ButtonVariantProps;
  color?: MuiButtonProps['color'];
  customColor?: string;
  customHoverColor?: string;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'customColor' && prop !== 'customHoverColor'
})<StyledButtonProps>(({ theme, variant, color, customColor, customHoverColor }) => {
  // Get base background and text colors from theme if using theme colors
  let baseBackgroundColor = '#00897B';
  let baseTextColor = '#fff';

  if (!customColor && (variant === 'contained' || variant === 'shadow' || variant === 'light') && color) {
    const colors = getColors(theme, color);
    baseBackgroundColor = colors.main;
    baseTextColor = colors.contrastText;
  }

  // For outlined/dashed/text variants
  if (variant === 'outlined' || variant === 'dashed' || variant === 'text') {
    baseBackgroundColor = 'transparent';
    baseTextColor = customColor || '#00897B';
    if (!customColor && color) {
      const colors = getColors(theme, color);
      baseTextColor = colors.main;
    }
  }

  // Override with custom color if provided
  if (customColor) {
    if (variant === 'contained' || !variant) {
      baseBackgroundColor = customColor;
      baseTextColor = '#fff';
    } else if (variant === 'outlined' || variant === 'dashed' || variant === 'text') {
      baseTextColor = customColor;
    }
  }

  return {
    // Base styles
    backgroundColor: baseBackgroundColor,
    color: baseTextColor,
    fontSize: '14px',
    borderRadius: '4px',
    height: '40px',
    fontWeight: 400,
    transition: 'opacity 0.3s ease',
    // Variant-specific borders
    ...(variant === 'outlined' && {
      border: '1px solid',
      borderColor: baseTextColor
    }),
    ...(variant === 'dashed' && {
      border: '1px dashed',
      borderColor: baseTextColor
    }),
    ...(variant === 'text' && {
      boxShadow: 'none'
    }),
    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      borderRadius: 4,
      opacity: 0,
      transition: 'all 0.5s'
    },
    '&:active::after': {
      position: 'absolute',
      borderRadius: 4,
      left: 0,
      top: 0,
      opacity: 1,
      transition: '0s'
    },
    // CRITICAL: Override ALL MUI hover states
    '&:hover': customHoverColor
      ? {
          backgroundColor: customHoverColor,
          color: '#fff',
          borderColor: customHoverColor,
          opacity: 1
        }
      : {
          // Keep everything the same, only change opacity
          backgroundColor: baseBackgroundColor,
          color: baseTextColor,
          ...(variant === 'outlined' && { borderColor: baseTextColor }),
          ...(variant === 'dashed' && { borderColor: baseTextColor }),
          opacity: 0.8
        },
    // Override MUI's default elevation changes
    '&.MuiButtonBase-root:hover': {
      boxShadow: 'none'
    },
    // Ensure disabled state doesn't interfere
    '&.Mui-disabled': {
      opacity: 0.5,
      backgroundColor: baseBackgroundColor,
      color: baseTextColor
    }
  };
});

// ==============================|| UI - BUTTON ||============================== //

export interface UIButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  /** Button variant type */
  variant?: ButtonVariantProps;
  /** Button color from theme palette */
  color?: MuiButtonProps['color'];
  /** Custom background color (overrides theme color) */
  customColor?: string;
  /** Custom hover background color */
  customHoverColor?: string;
  /** Icon to display at the start of button */
  startIcon?: ReactNode;
  /** Icon to display at the end of button */
  endIcon?: ReactNode;
  /** Image to display at the start (higher priority than startIcon) */
  startImage?: string;
  /** Image to display at the end (higher priority than endIcon) */
  endImage?: string;
  /** Alt text for images */
  imageAlt?: string;
  /** Size of the images in pixels */
  imageSize?: number;
  /** Loading state */
  loading?: boolean;
  /** Loading indicator position */
  loadingPosition?: 'start' | 'end' | 'center';
  /** Button content */
  children: ReactNode;
  /** Additional sx props */
  sx?: SxProps<Theme>;
  /** Full width button */
  fullWidth?: boolean;
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Disabled state */
  disabled?: boolean;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const Button = forwardRef(
  (
    {
      variant = 'contained',
      color = 'primary',
      customColor,
      customHoverColor,
      startIcon,
      endIcon,
      startImage,
      endImage,
      imageAlt = '',
      imageSize = 20,
      loading = false,
      loadingPosition = 'center',
      children,
      disabled = false,
      sx,
      ...others
    }: UIButtonProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const theme = useTheme();

    // Prepare start content (image or icon)
    const startContent = startImage ? (
      <img src={startImage} alt={imageAlt} style={{ width: imageSize, height: imageSize, marginRight: 8 }} />
    ) : (
      startIcon
    );

    // Prepare end content (image or icon)
    const endContent = endImage ? (
      <img src={endImage} alt={imageAlt} style={{ width: imageSize, height: imageSize, marginLeft: 8 }} />
    ) : (
      endIcon
    );

    // Loading indicator
    const loadingIndicator = <CircularProgress size={16} sx={{ color: 'inherit' }} />;

    // Determine what to show based on loading state
    const buttonStartIcon = loading && loadingPosition === 'start' ? loadingIndicator : startContent;
    const buttonEndIcon = loading && loadingPosition === 'end' ? loadingIndicator : endContent;
    const buttonChildren = loading && loadingPosition === 'center' ? loadingIndicator : children;

    return (
      <StyledButton
        ref={ref}
        variant={variant!}
        color={color}
        customColor={customColor}
        customHoverColor={customHoverColor}
        theme={theme}
        startIcon={buttonStartIcon}
        endIcon={buttonEndIcon}
        disabled={disabled || loading}
        disableElevation
        disableRipple
        sx={sx}
        {...others}
      >
        {buttonChildren}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
